import { Suspense, lazy, Component } from "react";
import styled, {
  keyframes,
  createGlobalStyle,
  css,
} from "styled-components/macro";
import { Switch, Route, Redirect, useHistory } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Main from "../../components/Main";
import Content from "../../components/Content";
import { Box, BoxContent } from "../../components/Box";
import UpgradeModal from "../../components/UpgradeModal";
import { Close, SkipioLogo } from "../../components/Icons";
import {
  touchSkipioApi,
  useSkipioApi,
  resetSkipioApiCache,
  isImpersonating,
} from "../../api";
import { useSSE } from "../../hooks/use-sse";
import PageSmall from "../../components/PageSmall";
import Button, { IconButton } from "../../components/Button";
import Stack from "../../components/Stack";
import IsOverPrompt from "../../components/account-management/IsOverPrompt";
import { MomentProvider } from "../../hooks/use-moment";
import AccountSetup from "../../components/account-management/AccountSetup";
import { StackingBoxModal } from "../../components/StackingBox";
import useLanguage from "../../hooks/use-language";
import { useTitlePrefix } from "../../hooks/use-title";
import usePlan from "../../hooks/use-plan";
import useFavicon from "../../hooks/use-favicon";
import EndTrialPrompt from "../../components/account-management/EndTrialPrompt";
import DemoEnded from "../../components/account-management/DemoEnded";
import { WARNING_COLOR, SUCCESS_COLOR } from "../../vars";
import Anchor from "../../components/Anchor";
import { useToast } from "../../hooks/use-toast";
import { useSkipioQuery } from "../../hooks/use-query";
import { queryClient } from "../../hooks/use-query";
import BusinessQuestionnaire from "../../components/account-management/BusinessQuestionnaire";
import formatPhoneNumber from "../../util/formatPhoneNumber";
import useUserGuiding from "../../hooks/use-user-guiding";
import SideNav from "../../components/Nav/SideNav";
import SideNavFallback from "../../components/Nav/SideNavFallback";
import { fetchUser } from "../../api/users";

const ResourceCenterRoute = lazy(() => import("./resource-center"));
const MessagesRoute = lazy(() => import("./messages"));
const ContactsRoute = lazy(() => import("./contacts"));
const Voicemails = lazy(() => import("./voicemails"));
const SkipLinksRoute = lazy(() => import("./skip-links"));
const Web2TxtRoute = lazy(() => import("./web2txt"));
const BounceReports = lazy(() => import("./bounce-reports"));
const CampaignsRoute = lazy(() => import("./campaigns"));
const CalendarRoute = lazy(() => import("./calendar"));
const CSVImportRoute = lazy(() => import("./csv-import"));
const ProfileRoute = lazy(() => import("./profile"));
const PlanRoute = lazy(() => import("./plan"));
const IntegrationsRoute = lazy(() => import("./integrations"));
const StartRoute = lazy(() => import("./start"));
const KeywordsRoute = lazy(() => import("./keywords"));
const PausedRoute = lazy(() =>
  import("../../components/account-management/AccountPaused")
);
const AdminRoute = lazy(() => import("./admin"));
const AccountLocked = lazy(() =>
  import("../../components/account-management/AccountLocked")
);
const SupportTicket = lazy(() =>
  import("../../components/HelpButton").then((module) => ({
    default: module.SupportTicket,
  }))
);

class AuthErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError(error) {
    // pass suspense calls through
    if (!(error instanceof Error)) throw error;

    const isAuthError =
      error.isAxiosError && error.response && error.response.status === 401;

    if (window.Rollbar && !isAuthError) {
      window.Rollbar.critical(error);
    }

    return {
      authError: isAuthError,
      hasError: true,
    };
  }
  render() {
    if (this.state.authError) {
      // @TODO renable this when login is released
      resetSkipioApiCache();
      queryClient.clear();
      this.props.history.push(
        `/login/${encodeURIComponent("Please sign in again.")}`
      );

      return null;
    }

    if (this.state.hasError) {
      return (
        <PageSmall>
          <div>
            <Center>
              <Stack>
                <></>
                <H2>An Error Has Occurred</H2>

                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
                <></>
              </Stack>
            </Center>
          </div>
        </PageSmall>
      );
    }

    return this.props.children;
  }
}

function AppRoute({ history }) {
  return (
    <>
      <BodyStyle />
      <AuthErrorBoundary history={history}>
        <Suspense
          fallback={
            <Main>
              <SideNavFallback />
              <Content>
                <Box>
                  <LoadingBoxContent>
                    <SkipioLogo />
                  </LoadingBoxContent>
                </Box>
              </Content>
            </Main>
          }
        >
          <AccountSetupSwitch />
        </Suspense>
      </AuthErrorBoundary>
    </>
  );
}

function AccountSetupSwitch() {
  const history = useHistory();
  const queryClient = useQueryClient();

  useSSE("notification", (notification) => {
    if (notification.payload.event === "inbound_sms_message_created") {
      touchSkipioApi("users");
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["messageThreads"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    }
  });

  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data;
  const plan = usePlan();

  useLanguage();
  useTitlePrefix(
    isImpersonating() ? `${user.firstName} ${user.lastName}` : "Skipio"
  );
  useFavicon();
  useUserGuiding();

  const hasTeamsDashboard =
    plan.features.teams &&
    (plan.isMultiUserOwner || user.teams.some((t) => t.isLeader));

  const hasFranchiseDashboard = Boolean(user.agencyOwner);
  const isPaused = plan.features.paused;

  if (user.account.disabledReason !== null && user.isTrueUser) {
    return (
      <Main>
        <SideNavFallback />
        <Content>
          <Suspense fallback={<Box />}>
            <AccountLocked />
          </Suspense>
        </Content>
      </Main>
    );
  }

  if (
    user.account.demoExpiresAt &&
    Date.parse(user.account.demoExpiresAt) < Date.now()
  ) {
    return (
      <Main>
        <SideNavFallback />
        <Content>
          <Suspense fallback={<Box />}>
            <DemoEnded />
          </Suspense>
        </Content>
      </Main>
    );
  }

  if (!user.skipioPhoneNumber) {
    return (
      <Main>
        <SideNavFallback />
        <Content>
          <Box>
            <StackingBoxModal>
              <AccountSetup
                numberQuery={user.phoneMobile.replace(/^\+1/, "").slice(0, 3)}
                onComplete={() => {
                  touchSkipioApi("users/me");
                  queryClient.invalidateQueries({ queryKey: ["users", "me"] });
                }}
              />
            </StackingBoxModal>
          </Box>
        </Content>
      </Main>
    );
  }

  if (
    plan.isAccountOwner &&
    !user.email.includes("@skipio.com") &&
    !user.account.ignoreRegistrationStatus &&
    user.account.registrationStatus === "new"
  ) {
    return (
      <Main>
        <SideNavFallback />
        <Content>
          <Suspense fallback={<Box />}>
            <BusinessQuestionnaire />
          </Suspense>
        </Content>
      </Main>
    );
  }

  return (
    <Switch>
      <Route path="/app">
        <Main>
          <Suspense
            fallback={
              <>
                <SideNavFallback />
                <Content>
                  <Box>
                    <LoadingBoxContent>
                      <SkipioLogo />
                    </LoadingBoxContent>
                  </Box>
                </Content>
              </>
            }
          >
            <MomentProvider>
              <RegistrationStatusBanner />
              <AutoReplyBanner />
              <SideNav history={history} />
              <Content>
                <Suspense
                  fallback={
                    <Box>
                      <LoadingBoxContent>
                        <SkipioLogo />
                      </LoadingBoxContent>
                    </Box>
                  }
                >
                  <Switch>
                    <Route path="/app/start" component={StartRoute} />
                    <Route
                      path="/app/success-center/post/:id"
                      render={({
                        match: {
                          params: { id },
                        },
                      }) => <Redirect to={`/app/resource-center/post/${id}`} />}
                    />
                    <Route path="/app/success-center">
                      <Redirect to="/app/resource-center" />
                    </Route>
                    <Route
                      path="/app/resource-center"
                      component={ResourceCenterRoute}
                    />
                    <Route
                      path="/app/voicemails/:recordingId?"
                      component={Voicemails}
                    />
                    <Route
                      path="/app/links/:linkId?"
                      component={SkipLinksRoute}
                    />
                    <Route
                      path="/app/web2txt/:formId?"
                      component={Web2TxtRoute}
                    />
                    <Route
                      path="/app/messages/:scope?/:contactId?"
                      component={MessagesRoute}
                    />
                    <Route
                      path="/app/contacts/:category?/:itemId?"
                      component={ContactsRoute}
                    />
                    <Route path="/app/csv-import" component={CSVImportRoute} />
                    <Route
                      path="/app/campaigns/:campaignId?"
                      component={CampaignsRoute}
                    />
                    <Route path="/app/calendar" component={CalendarRoute} />
                    <Route path="/app/keywords" component={KeywordsRoute} />
                    <Route path="/app/profile" component={ProfileRoute} />
                    <Route
                      path={[
                        "/app/bounce-reports/:reportId?",
                        "/app/bounce-reports",
                      ]}
                      component={BounceReports}
                    />
                    <Route
                      path="/app/integrations/:integrationId?"
                      component={IntegrationsRoute}
                    />
                    <Route path="/app/admin" component={AdminRoute} />
                    <Route path="/app/plan" component={PlanRoute} />
                    <Route path="/app/support" component={SupportTicket} />
                    <Route path="/app/start" component={StartRoute} />
                    <Route path="/app/paused" component={PausedRoute} />
                    {isPaused ? (
                      <Redirect to="/app/paused" />
                    ) : hasTeamsDashboard ? (
                      <Redirect to="/app/admin/team" />
                    ) : hasFranchiseDashboard ? (
                      <Redirect to="/app/admin/franchise-dashboard" />
                    ) : (
                      <Redirect to="/app/messages" />
                    )}
                  </Switch>
                </Suspense>
              </Content>
            </MomentProvider>
            <UpgradeModal />
            <IsOverPrompt />
            <EndTrialPrompt />
          </Suspense>
        </Main>
      </Route>
    </Switch>
  );
}

function RegistrationStatusBanner() {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data;
  const { registrationStatus, ignoreRegistrationStatus } = user.account;
  const { registrationAcknowledged } = useSkipioQuery({
    queryKey: ["registrationAcknowledgement"],
  }).data;

  const acknowledgeRegistration = async () => {
    await skipioApi.registrationAcknowledgement.put(
      {},
      { data: { registrationAcknowledged: true } }
    );

    touchSkipioApi("registrationAcknowledgement");
    queryClient.invalidateQueries({
      queryKey: ["registrationAcknowledgement"],
    });
  };

  if (!registrationStatus || ignoreRegistrationStatus) {
    return null;
  }

  if (registrationStatus !== "activated") {
    return (
      <NotificationBanner>
        <span>
          Your registration is pending. Any messages you send will not be
          delivered.
        </span>
      </NotificationBanner>
    );
  } else {
    if (!registrationAcknowledged) {
      return (
        <NotificationBanner
          style={{ justifyContent: "flex-end" }}
          variant="success"
        >
          <span style={{ flex: 1, textAlign: "center" }}>
            Your registration is complete. You may now send messages.
          </span>
          <IconButton onClick={acknowledgeRegistration}>
            <Close />
          </IconButton>
        </NotificationBanner>
      );
    } else {
      return null;
    }
  }
}

function AutoReplyBanner() {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data;
  const showToast = useToast();
  const providerPhoneNumber = useSkipioQuery({
    queryKey: ["providerPhoneNumbers", user.providerPhoneNumbers[0].id],
    enabled: user.providerPhoneNumbers.length > 0,
  }).data;

  useSSE("notification", (notification) => {
    if (notification.payload.event === "provider-phone-number-updated") {
      queryClient.invalidateQueries({ queryKey: ["providerPhoneNumbers"] });
      const { changes } = notification.payload;
      if ("autoResponderEnabled" in changes) {
        showToast(
          `Auto-Reply ${
            changes.autoResponderEnabled[1] ? "enabled" : "disabled"
          }`,
          {
            success: true,
          }
        );
      }
    }
  });

  const disableAutoReply = async () => {
    await skipioApi.providerPhoneNumbers[providerPhoneNumber.id].put(
      {},
      { data: { providerPhoneNumber: { autoResponderEnabled: false } } }
    );
    showToast("Auto-Reply disabled", { success: true });
    queryClient.invalidateQueries({ queryKey: ["providerPhoneNumbers"] });
  };

  if (!providerPhoneNumber || !providerPhoneNumber.autoResponderEnabled)
    return null;

  return (
    <NotificationBanner>
      <span>
        {`Auto-Reply enabled for ${formatPhoneNumber(
          providerPhoneNumber.phoneNumber
        )} - `}
        <Anchor href="#" onClick={disableAutoReply}>
          Disable
        </Anchor>
      </span>
    </NotificationBanner>
  );
}

export default AppRoute;

const loadingAnim = keyframes`
 from {
   opacity: 0;
 }
 to {
   opacity: 1;
 }
`;

const BodyStyle = createGlobalStyle`
  body {
    background: #0a344a;
  }
`;

const LoadingBoxContent = styled(BoxContent)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  ${SkipioLogo} {
    width: 100px;
    height: 100px;
    color: #dcdcdc;
    margin-bottom: 100px;
    animation: ${loadingAnim} 1s infinite alternate;
    animation-delay: 1s;
    animation-fill-mode: both;
  }
`;

const H2 = styled.h2`
  font-size: 21px;
  font-weight: 300;
  color: #0a344a;
`;

const Center = styled.div`
  text-align: center;
`;

const NotificationBanner = styled.div`
  grid-area: notification;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  background: ${(p) =>
    p.variant === "success" ? SUCCESS_COLOR : WARNING_COLOR};
  padding: 5px 20px;
  cursor: default;
  ${(p) =>
    p.variant === "success" &&
    css`
      & span {
        color: white;
      }
    `}
`;
